// Écran « Mon travail » : état de la synchronisation (dernier contact serveur, attente, conflits,
// déclenchement manuel), missions du jour, tournées de la semaine, ordres de mission, chacun avec
// son état local (enregistré sur cet appareil, synchronisé, conflit). Tout vient du stockage de
// l'appareil ; le téléchargement initial exige le réseau et le dit.
import type { PrioriteIntervention } from '@cc/contrats';
import { Bouton, EtatVide, Squelette } from '@cc/ui';
import { Download, WifiOff } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { messageErreur } from '../../api/erreurs';
import type { EtatPwa } from '../../terrain/pwa';
import { LIBELLES_ETAT_MISSION, LIBELLES_ETAT_ORDRE, LIBELLES_PRIORITE, LIBELLES_REALISATION, dateHeure, dateLongue, depuis, etatLocalObjet } from '../../terrain/etat-local';
import { synchroniseurPartage, type BilanSynchronisation, type PhaseSynchronisation } from '../../terrain/synchronisation';
import { useEnLigne, useOperationsLocales, useSynchronisation, useTravailLocal } from '../../terrain/useTerrain';
import { Bloc, Definitions, EnTeteTerrain, EtatLocal, Ligne, Liste, Section, TexteEtat } from './commun';
import { useZoneTerrain } from './CoquilleTerrain';
import styles from './terrain.module.css';
import { PlacesEmportees } from './PlacesEmportees';
import { IlotsEmportes } from './IlotsEmportes';

const ORDRE_PRIORITE: Record<PrioriteIntervention, number> = { urgente: 0, haute: 1, normale: 2, basse: 3 };
const LIBELLES_PHASE: Record<PhaseSynchronisation, string> = { repos: 'Synchronisation en cours', envoi_operations: 'Envoi des opérations en cours', envoi_photos: 'Envoi des photos en cours', telechargement: 'Téléchargement du travail en cours' };

function resumeBilan(b: BilanSynchronisation): string {
  if (b.ignoree === 'hors_ligne') return 'Hors connexion : rien n\'a pu être envoyé.';
  if (b.erreur) return `Synchronisation interrompue : ${b.erreur}`;
  const parts = [
    b.appliquees + b.doublons > 0 ? `${b.appliquees + b.doublons} opération(s) reçue(s)` : null,
    b.conflits > 0 ? `${b.conflits} en conflit` : null,
    b.rejetees > 0 ? `${b.rejetees} rejetée(s)` : null,
    b.photosEnvoyees > 0 ? `${b.photosEnvoyees} photo(s) envoyée(s)` : null,
    b.photosEnEchec > 0 ? `${b.photosEnEchec} photo(s) en échec` : null,
    b.travailTelecharge ? 'travail mis à jour' : null,
  ].filter(Boolean);
  return parts.length ? `Synchronisation terminée : ${parts.join(', ')}.` : 'Synchronisation terminée : rien à envoyer, travail à jour.';
}

/**
 * Ouverture de l'application sans réseau (D-137) : la coquille n'est en cache qu'une fois le service
 * worker actif ; tant que la préparation n'est pas finie, l'agent doit garder le réseau.
 */
function etatOuvertureSansReseau(pwa: EtatPwa): ReactNode {
  if (!pwa.supporte) return 'Non prise en charge par ce navigateur';
  if (pwa.pretHorsConnexion) return <TexteEtat libelle="Prête sur cet appareil" ton="ok" />;
  if (pwa.erreur) return <TexteEtat libelle={pwa.erreur} ton="attention" />;
  if (import.meta.env.DEV) return 'Version construite seulement';
  return <TexteEtat libelle="Préparation en cours : gardez le réseau jusqu'à « Prête sur cet appareil »" ton="attention" />;
}

export function BlocSynchronisation({ ouvertureSansReseau = false }: { ouvertureSansReseau?: boolean }) {
  const { pwa } = useZoneTerrain();
  const sync = useSynchronisation();
  const enLigne = useEnLigne();
  const [message, setMessage] = useState<string | null>(null);
  const lancer = async () => {
    setMessage(null);
    try {
      setMessage(resumeBilan(await synchroniseurPartage().synchroniser()));
    } catch (e) {
      setMessage(messageErreur(e));
    }
  };
  const h = sync.horodatages;
  return (
    <Bloc etiquette="Synchronisation" titre={sync.attente.operations + sync.attente.photos > 0 ? `${sync.attente.operations} opération(s) et ${sync.attente.photos} photo(s) en attente` : 'Rien en attente sur cet appareil'}>
      <Definitions
        elements={[
          { terme: 'Dernier contact serveur', valeur: depuis(h.dernier_contact_serveur) },
          { terme: 'Dernière synchronisation', valeur: dateHeure(h.derniere_synchronisation) },
          { terme: 'Travail téléchargé', valeur: dateHeure(h.travail_telecharge_le) },
          { terme: 'Conflits', valeur: sync.attente.conflits > 0 ? <TexteEtat libelle={`${sync.attente.conflits} à trancher par le responsable`} ton="attention" /> : 'aucun' },
          ...(ouvertureSansReseau ? [{ terme: 'Ouverture sans réseau', valeur: etatOuvertureSansReseau(pwa) }] : []),
        ]}
      />
      <div className={styles.ligneBoutons}>
        <Bouton onClick={() => void lancer()} chargement={sync.enCours} libelleChargement={LIBELLES_PHASE[sync.phase]} disabled={!enLigne} icone={enLigne ? Download : WifiOff}>
          Synchroniser maintenant
        </Bouton>
        {!enLigne ? <span className={styles.aide}>Hors connexion : la synchronisation reprendra d&apos;elle-même au retour du réseau.</span> : null}
      </div>
      {sync.derniereErreur && !message ? <TexteEtat libelle={`Dernière tentative : ${sync.derniereErreur}`} ton="attention" /> : null}
      {message ? (
        <p role="status" className={styles.aide}>
          {message}
        </p>
      ) : null}
    </Bloc>
  );
}

export function PageMonTravail() {
  const { session } = useZoneTerrain();
  const travail = useTravailLocal();
  const operations = useOperationsLocales();
  const sync = useSynchronisation();
  const enLigne = useEnLigne();
  const ops = operations.donnees ?? [];
  const missions = [...(travail.donnees?.missions ?? [])].sort((a, b) => ORDRE_PRIORITE[a.intervention.priorite] - ORDRE_PRIORITE[b.intervention.priorite] || a.date.localeCompare(b.date));
  const categories = new Map((travail.donnees?.referentiels.categories ?? []).map((c) => [c.code, c.libelle]));

  return (
    <>
      <EnTeteTerrain titre="Mon travail" sousTitre={`${session.nom}${session.collectivite ? ` · ${session.collectivite}` : ''}${travail.donnees?.agent?.equipes.length ? ` · ${travail.donnees.agent.equipes.map((e) => e.nom).join(', ')}` : ''}`} />
      <BlocSynchronisation ouvertureSansReseau />
      <PlacesEmportees />
      <IlotsEmportes />
      {travail.chargement ? (
        <Squelette lignes={5} libelle="Lecture du travail enregistré sur l'appareil" />
      ) : !travail.donnees ? (
        <EtatVide titre="Aucun travail sur cet appareil" niveau={2} action={enLigne ? <Bouton onClick={() => void synchroniseurPartage().synchroniser()} chargement={sync.enCours} libelleChargement="Téléchargement du travail en cours">Télécharger mon travail</Bouton> : undefined}>
          <p>{enLigne ? 'Téléchargez vos missions, tournées et ordres de mission pour travailler ensuite sans réseau.' : 'Hors connexion : le premier téléchargement de vos missions exige le réseau.'}</p>
        </EtatVide>
      ) : (
        <>
          <Section titre="Missions du jour" droite={<span className={styles.aide}>{missions.length}</span>}>
            {missions.length === 0 ? (
              <p className={styles.aide}>Aucune mission affectée.</p>
            ) : (
              <Liste libelle="Missions">
                {missions.map((m) => (
                  <Ligne
                    key={m.id}
                    href={`/terrain/missions/${m.id}`}
                    titre={`${m.intervention.numero} · ${m.intervention.titre}`}
                    sousTitre={[LIBELLES_PRIORITE[m.intervention.priorite], categories.get(m.intervention.categorie) ?? m.intervention.categorie, m.intervention.adresse, LIBELLES_ETAT_MISSION[m.etat]].filter(Boolean).join(' · ')}
                    droite={<EtatLocal etat={etatLocalObjet(ops, m.id)} />}
                  />
                ))}
              </Liste>
            )}
          </Section>
          <Section titre="Tournées de la semaine" droite={<span className={styles.aide}>{travail.donnees.tournees.length}</span>}>
            {travail.donnees.tournees.length === 0 ? (
              <p className={styles.aide}>Aucune tournée cette semaine.</p>
            ) : (
              <Liste libelle="Tournées">
                {travail.donnees.tournees.map((t) => (
                  <Ligne
                    key={t.id}
                    href={`/terrain/tournees/${t.id}`}
                    titre={`${t.code} · ${t.nom}`}
                    sousTitre={`${dateLongue(t.date)} ${t.heure} · ${t.secteur.nom} · ${t.points.length} points${t.realisation ? ` · ${LIBELLES_REALISATION[t.realisation.etat]}` : ''}`}
                    droite={<EtatLocal etat={etatLocalObjet(ops, t.id)} />}
                  />
                ))}
              </Liste>
            )}
          </Section>
          <Section titre="Ordres de mission" droite={<span className={styles.aide}>{travail.donnees.ordres_mission.length}</span>}>
            {travail.donnees.ordres_mission.length === 0 ? (
              <p className={styles.aide}>Aucun ordre de mission.</p>
            ) : (
              <Liste libelle="Ordres de mission">
                {travail.donnees.ordres_mission.map((o) => (
                  <Ligne key={o.id} href={`/terrain/ordres/${o.id}`} titre={`${o.numero} · ${o.objet_libelle}`} sousTitre={`Du ${dateLongue(o.periode_debut)} au ${dateLongue(o.periode_fin)} · ${LIBELLES_ETAT_ORDRE[o.etat]}`} droite={<EtatLocal etat={etatLocalObjet(ops, o.id)} />} />
                ))}
              </Liste>
            )}
          </Section>
          <p className={styles.aide}>Travail reçu du serveur le {dateHeure(travail.donnees.horodatage)}, enregistré sur cet appareil le {dateHeure(travail.donnees.telecharge_le)}.</p>
        </>
      )}
    </>
  );
}
